import Image from "next/image";
import Link from "next/link";
import React from "react";
import styled from "styled-components";
import Heading from "../heading/Heading";
import Text from "../text/Text";
import commit from "../../public/github/commit.png";
import filename from "../../public/github/filename.png";
import fork from "../../public/github/fork.png";
import createFile from "../../public/github/new-file.png";
import openPR from "../../public/github/openpr.png";

const Para = styled(Text)``;
Para.defaultProps = { ...(Text.defaultProps ?? {}), element: "p" };

const ContributionInstructionsElement = styled.div``;

type Props = {};
const ContributionInstructions: React.FC<Props> = () => {
  return (
    <ContributionInstructionsElement>
      <Heading level="1">Contribute</Heading>
      <section>
        <Para>
          We want to make contributing to this project as easy as possible. This
          guide aims to help you get your first contribution published on
          Essential Cardano. All of the content on this website is stored as
          Markdown files
          <Link href="https://github.com/input-output-hk/essential-cardano-content">
            <a>on Github</a>
          </Link>
          . To add a new piece of content you&apos;ll have to submit a pull
          request for us to review.
        </Para>

        <Para>
          The files are organised based on their type:
          <ul>
            <li>
              Articles are in{" "}
              <Link href="https://github.com/input-output-hk/essential-cardano-content/tree/main/content/article">
                content/article
              </Link>
            </li>
            <li>
              Infographics are in{" "}
              <Link href="https://github.com/input-output-hk/essential-cardano-content/tree/main/content/infographic">
                content/infographic
              </Link>
            </li>
            <li>
              Podcasts are in{" "}
              <Link href="https://github.com/input-output-hk/essential-cardano-content/tree/main/content/podcast">
                content/podcast
              </Link>
            </li>
            <li>
              Videos are in{" "}
              <Link href="https://github.com/input-output-hk/essential-cardano-content/tree/main/content/video">
                content/video
              </Link>
            </li>
            <li>
              External links are in{" "}
              <Link href="https://github.com/input-output-hk/essential-cardano-content/tree/main/content/external">
                content/external
              </Link>
            </li>
          </ul>
          You will need to find the category that best matches your
          contribution.
        </Para>

        <Para>
          We&apos;ve designed this guide so that you can follow it using only
          the Github Website. If you have technical experience using Git, then
          you may use any Git client you are familiar with in order to submit
          your contribution.
        </Para>
      </section>

      <section>
        <Heading level="2">Pre-requisites</Heading>

        <Heading level="3">Register for Github</Heading>

        <Para>
          In order to contribute you will need to register for a Github account.
          This is free and can be done on this page.
        </Para>

        <Para>
          Make sure you register with the same email address you&apos;ve used on
          Essential Cardano. This way we can link your accounts together and
          show your contributor status on the website.
        </Para>

        <Heading level="3">Fork it 🍴</Heading>

        <Para>
          This repository is locked down so in order to contribute you will have
          to &ldquo;fork&rdquo; it under your Github account. This process will
          effectively create a copy of it just for your use.
        </Para>

        <Image src={fork} alt="fork the repository" />
      </section>
      <section>
        <Heading level="2">Creating your contribution</Heading>
        <Para>
          On your fork open the folder that best describes the type of content
          you want to contribute, e.g. if you want to add a new article then
          open content/article. Once your satifies with your chosen category,
          click the &ldquo;Create New File&rdquo; button in the corner.
        </Para>
        <Image src={createFile} alt="create new file on Github" />

        <Para>Copy and paste the following template to get you started</Para>
        <pre>
          {`---
title: A title for this content
tags: descentralisation
url:
image: https://ucarecdn.com/759bae9f-eb5a-4700-b83e-b3084f834596/
image_text: some text describing the image above
---

Your main content. This will get shown on the Essential Cardano Website
`}
        </pre>

        <Para>
          The part at the top, the one between the dashes is called the
          &ldquo;header&rdquo; for this file. We use it to specify the
          content&apos;s title, an important images that highly relevant to the
          content and any tags for this file.
        </Para>

        <Para>
          The part after the header is the main body. You can use Markdown to
          write antyhing here.
        </Para>

        <Para>Finally, give your file a name.</Para>

        <Image src={filename} alt="name your file" />

        <Para>When you&apos;re ready, go ahead and commit the file.</Para>

        <Image src={commit} alt="commit" />
      </section>

      <section>
        <Heading level="2">Submitting a pull request</Heading>
        <Para>
          Now that you have commited your new file, you&apos;re ready to
          contribute it to Essential Cardano.
        </Para>

        <Image src={openPR} alt="open a Pull request on Github" />
      </section>

      <section>
        <Heading level="2">What&apos;s next</Heading>
        <Para>
          Next, someone from the team will review and approve your changes. Once
          that happens the content will automatically be published on the
          website.
        </Para>
        <Para>
          Thanks for taking the time to contribute to essentialcardano.io
        </Para>
      </section>
    </ContributionInstructionsElement>
  );
};

export default ContributionInstructions;
